/**
 * Escapes a CSV cell value according to RFC 4180.
 * Values containing commas, double quotes, or newlines are wrapped in double quotes,
 * and any existing double quotes are escaped by doubling them.
 */
const escapeCSVCell = (value: string): string => {
  if (value.includes(",") || value.includes('"') || value.includes("\n") || value.includes("\r")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

/**
 * Converts a 2D array of strings into CSV content and triggers a browser file download.
 *
 * Each inner array represents a row, and each string within represents a cell value.
 * Cell values are escaped according to RFC 4180 — values containing commas, double quotes,
 * or newlines are automatically quoted and escaped.
 *
 * @param data - A 2D array of strings where each inner array is a row of cell values
 * @param filename - The name for the downloaded file (defaults to "download.csv")
 *
 * @example
 * ```ts
 * downloadCSV({
 *   data: [
 *     ["Name", "Email", "Role"],
 *     ["Alice", "alice@example.com", "Admin"],
 *     ["Bob", "bob@example.com", "User"],
 *   ],
 *   filename: "users.csv",
 * });
 * ```
 */
export const downloadCSV = ({ data, filename = "download.csv" }: { data: string[][]; filename?: string }) => {
  const csvContent = data.map((row) => row.map(escapeCSVCell).join(",")).join("\n");

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};
